import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Trash2, Bell, BellOff, Zap, Send, Download, KanbanSquare, LayoutGrid } from "lucide-react";
import Layout from "../components/Layout";
import api, { errMessage, tokenStore } from "../lib/api";
import BlastBuyersModal from "../components/BlastBuyersModal";

const STAGES = [
  { id: "lead", label: "Lead", color: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  { id: "contacted", label: "Contacted", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "offered", label: "Offered", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { id: "under_contract", label: "Under contract", color: "bg-violet-50 text-violet-700 border-violet-200" },
  { id: "closed", label: "Closed", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "dead", label: "Dead", color: "bg-rose-50 text-rose-700 border-rose-200" },
];

const STAGE_BY_ID = Object.fromEntries(STAGES.map((s) => [s.id, s]));

async function downloadCsv(path, filename) {
  // Use fetch so we can include Authorization header (axios isn't ideal for binary downloads).
  const url = (process.env.REACT_APP_BACKEND_URL || "") + path;
  const r = await fetch(url, { headers: { Authorization: `Bearer ${tokenStore.get()}` } });
  if (!r.ok) throw new Error(`${r.status}`);
  const blob = await r.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

export default function Saved() {
  const [searches, setSearches] = useState([]);
  const [deals, setDeals] = useState([]);
  const [tab, setTab] = useState("searches");
  const [scanning, setScanning] = useState(null);
  const [blastProp, setBlastProp] = useState(null);
  const [view, setView] = useState("kanban"); // 'kanban' | 'list'
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([
        api.get("/saved/searches"),
        api.get("/saved/deals"),
      ]);
      setSearches(s.data);
      setDeals(d.data);
    } catch (e) {
      toast.error(errMessage(e, "Could not load saved items"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const delSearch = async (id) => {
    await api.delete(`/saved/searches/${id}`);
    toast.success("Removed");
    load();
  };

  const delDeal = async (id) => {
    await api.delete(`/saved/deals/${id}`);
    toast.success("Removed");
    load();
  };

  const moveDeal = async (id, stage) => {
    // Optimistic update
    setDeals((prev) => prev.map((d) => (d.id === id ? { ...d, pipeline_stage: stage } : d)));
    try {
      await api.patch(`/saved/deals/${id}`, { pipeline_stage: stage });
    } catch (e) {
      toast.error(errMessage(e, "Could not move"));
      load();
    }
  };

  const toggleAlerts = async (s) => {
    const next = !s.alerts_enabled;
    try {
      await api.patch(`/saved/searches/${s.id}/alerts`, { alerts_enabled: next });
      toast.success(next ? "Alerts on — daily scan at 8am CT" : "Alerts off");
      load();
    } catch (e) {
      toast.error(errMessage(e, "Could not update alerts"));
    }
  };

  const scanNow = async (s) => {
    setScanning(s.id);
    try {
      const r = await api.post(`/alerts/scan/${s.id}`);
      if (r.data.fired) {
        toast.success(`New: ${r.data.alert.summary}`);
      } else {
        toast.info(r.data.message || "No new signals yet");
      }
      load();
    } catch (e) {
      toast.error(errMessage(e, "Scan failed"));
    } finally {
      setScanning(null);
    }
  };

  const exportCsv = async (kind) => {
    try {
      const map = {
        deals: ["/api/export/deals.csv", "wholesalehq_deals.csv"],
        buyers: ["/api/export/buyers.csv", "wholesalehq_buyers.csv"],
        searches: ["/api/export/searches.csv", "wholesalehq_markets.csv"],
      };
      const [path, fn] = map[kind];
      await downloadCsv(path, fn);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(errMessage(e, "Export failed"));
    }
  };

  return (
    <Layout>
      <div className="px-8 py-10 max-w-7xl">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="overline mb-3">// Library</div>
            <h1 className="font-display text-4xl font-bold tracking-tighter">Saved.</h1>
          </div>
          {tab === "deals" && (
            <div className="flex gap-2">
              <div className="inline-flex border border-[--line] bg-[--bg-soft] p-0.5">
                <button
                  type="button"
                  data-testid="deals-view-kanban"
                  onClick={() => setView("kanban")}
                  className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${
                    view === "kanban" ? "bg-[--ink] text-white" : "text-[--ink-soft] hover:text-[--ink]"
                  }`}
                >
                  <KanbanSquare size={12} strokeWidth={1.5} /> Pipeline
                </button>
                <button
                  type="button"
                  data-testid="deals-view-list"
                  onClick={() => setView("list")}
                  className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${
                    view === "list" ? "bg-[--ink] text-white" : "text-[--ink-soft] hover:text-[--ink]"
                  }`}
                >
                  <LayoutGrid size={12} strokeWidth={1.5} /> Grid
                </button>
              </div>
              <button
                data-testid="export-deals-csv"
                onClick={() => exportCsv("deals")}
                disabled={deals.length === 0}
                className="btn-ghost flex items-center gap-1.5 text-xs disabled:opacity-40"
              >
                <Download size={12} strokeWidth={1.5} /> Export CSV
              </button>
            </div>
          )}
          {tab === "searches" && (
            <button
              data-testid="export-markets-csv"
              onClick={() => exportCsv("searches")}
              disabled={searches.length === 0}
              className="btn-ghost flex items-center gap-1.5 text-xs disabled:opacity-40"
            >
              <Download size={12} strokeWidth={1.5} /> Export CSV
            </button>
          )}
        </div>

        <div className="flex gap-1 border-b border-[--line] mb-6">
          {[
            { id: "searches", label: `Markets (${searches.length})` },
            { id: "deals", label: `Deals (${deals.length})` },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-2 text-sm border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-[--ink] text-[--ink] font-medium"
                  : "border-transparent text-[--ink-muted] hover:text-[--ink]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "searches" && (
          <div className="space-y-2" data-testid="saved-searches-list">
            {searches.length === 0 && (
              <div className="text-sm text-[--ink-muted] py-12 text-center border border-dashed border-[--line]">
                No saved searches. Run an analysis from the dashboard and tap “Save search”.
              </div>
            )}
            {searches.map((s) => {
              const params = new URLSearchParams({
                city: s.city,
                county: s.county || "",
                state: s.state,
              });
              return (
                <div key={s.id} className="surface-card p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-display font-bold text-base">{s.label}</div>
                      {s.alerts_enabled && (
                        <span
                          data-testid={`alerts-badge-${s.id}`}
                          className="text-[10px] px-1.5 py-0.5 font-medium bg-[--accent] text-white uppercase tracking-wider"
                        >
                          Alerts on
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[--ink-muted] font-mono mt-0.5">
                      {s.city} · {s.county || "—"} · {s.state}
                      {s.last_scanned_at && (
                        <span className="ml-2 text-[--ink-muted]">
                          · last scan {new Date(s.last_scanned_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      data-testid={`toggle-alerts-${s.id}`}
                      onClick={() => toggleAlerts(s)}
                      className={`!py-1.5 !px-3 text-xs flex items-center gap-1 ${
                        s.alerts_enabled ? "btn-primary" : "btn-ghost"
                      }`}
                      title={s.alerts_enabled ? "Disable alerts" : "Enable alerts"}
                    >
                      {s.alerts_enabled ? <Bell size={12} strokeWidth={1.5} /> : <BellOff size={12} strokeWidth={1.5} />}
                    </button>
                    <button
                      data-testid={`scan-now-${s.id}`}
                      onClick={() => scanNow(s)}
                      disabled={scanning === s.id}
                      className="btn-ghost !py-1.5 !px-3 text-xs flex items-center gap-1"
                      title="Scan now"
                    >
                      <Zap size={12} strokeWidth={1.5} />
                      {scanning === s.id ? "…" : "Scan"}
                    </button>
                    <button
                      data-testid={`delete-search-${s.id}`}
                      onClick={() => delSearch(s.id)}
                      className="btn-ghost !py-1.5 !px-3 text-xs flex items-center gap-1"
                    >
                      <Trash2 size={12} strokeWidth={1.5} />
                    </button>
                    <Link
                      to={`/market?${params.toString()}`}
                      data-testid={`open-search-${s.id}`}
                      className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1"
                    >
                      Open <ArrowRight size={12} strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "deals" && view === "kanban" && (
          <div data-testid="deals-kanban" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto">
            {STAGES.map((stage) => {
              const list = deals.filter((d) => (d.pipeline_stage || "lead") === stage.id);
              return (
                <div
                  key={stage.id}
                  data-testid={`stage-col-${stage.id}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (dragId) moveDeal(dragId, stage.id);
                    setDragId(null);
                  }}
                  className={`flex flex-col border ${stage.color} min-h-[120px]`}
                >
                  <div className={`flex items-center justify-between px-3 py-2 border-b ${stage.color} text-[11px] font-mono uppercase tracking-wider font-semibold`}>
                    <span>{stage.label}</span>
                    <span className="opacity-60">{list.length}</span>
                  </div>
                  <div className="p-2 space-y-2 flex-1">
                    {list.length === 0 && (
                      <div className="text-[10px] font-mono text-[--ink-muted] text-center py-4 opacity-60">
                        drop here
                      </div>
                    )}
                    {list.map((d) => (
                      <DealCard
                        key={d.id}
                        deal={d}
                        onDragStart={() => setDragId(d.id)}
                        onDragEnd={() => setDragId(null)}
                        onMove={(stage) => moveDeal(d.id, stage)}
                        onBlast={() => setBlastProp(d.property_data)}
                        onDelete={() => delDeal(d.id)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "deals" && view === "list" && (
          <div className="grid md:grid-cols-2 gap-3" data-testid="saved-deals-list">
            {deals.length === 0 && (
              <div className="md:col-span-2 text-sm text-[--ink-muted] py-12 text-center border border-dashed border-[--line]">
                No saved deals yet.
              </div>
            )}
            {deals.map((d) => {
              const p = d.property_data;
              const stage = STAGE_BY_ID[d.pipeline_stage || "lead"];
              return (
                <div key={d.id} className="surface-card p-4">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <div className="font-display font-bold text-base truncate">{p.address}</div>
                      <div className="text-xs text-[--ink-muted] font-mono mt-0.5">
                        {p.city}, {p.state} {p.zip}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        data-testid={`blast-deal-${d.id}`}
                        onClick={() => setBlastProp(p)}
                        className="text-[--ink-muted] hover:text-[--accent]"
                        title="Blast to buyers"
                      >
                        <Send size={14} strokeWidth={1.5} />
                      </button>
                      <button
                        data-testid={`delete-deal-${d.id}`}
                        onClick={() => delDeal(d.id)}
                        className="text-[--ink-muted] hover:text-[--danger]"
                      >
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-[10px] px-1.5 py-0.5 font-mono uppercase tracking-wider border ${stage.color}`}>
                      {stage.label}
                    </span>
                    <select
                      data-testid={`stage-select-${d.id}`}
                      value={d.pipeline_stage || "lead"}
                      onChange={(e) => moveDeal(d.id, e.target.value)}
                      className="text-xs flex-1"
                    >
                      {STAGES.map((s) => (
                        <option key={s.id} value={s.id}>
                          Move to {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs border-t border-[--line] pt-3">
                    <div>
                      <div className="overline text-[10px] mb-1">ARV</div>
                      <div className="font-mono font-semibold">
                        ${((p.estimated_arv || 0) / 1000).toFixed(0)}k
                      </div>
                    </div>
                    <div>
                      <div className="overline text-[10px] mb-1">MAO 70</div>
                      <div className="font-mono font-semibold text-[--accent]">
                        ${((p.mao_70 || 0) / 1000).toFixed(0)}k
                      </div>
                    </div>
                    <div>
                      <div className="overline text-[10px] mb-1">Profit</div>
                      <div className="font-mono font-semibold text-[--success]">
                        ${((p.potential_profit || 0) / 1000).toFixed(0)}k
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {blastProp && (
        <BlastBuyersModal property={blastProp} onClose={() => setBlastProp(null)} />
      )}
    </Layout>
  );
}

function DealCard({ deal, onDragStart, onDragEnd, onMove, onBlast, onDelete }) {
  const p = deal.property_data || {};
  return (
    <div
      data-testid={`deal-card-${deal.id}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className="bg-white border border-[--line] p-2.5 cursor-move hover:border-[--ink] transition-colors"
    >
      <div className="font-semibold text-[12px] truncate">{p.address}</div>
      <div className="text-[10px] font-mono text-[--ink-muted] truncate">
        {p.city}, {p.state}
      </div>
      <div className="flex items-center justify-between mt-2 text-[10px] font-mono">
        <span className="text-[--ink-muted]">MAO</span>
        <span className="font-semibold">${((p.mao_70 || 0) / 1000).toFixed(0)}k</span>
      </div>
      <div className="flex items-center justify-between text-[10px] font-mono">
        <span className="text-[--ink-muted]">Profit</span>
        <span className="font-semibold text-emerald-700">${((p.potential_profit || 0) / 1000).toFixed(0)}k</span>
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-[--line]">
        <select
          data-testid={`kanban-stage-${deal.id}`}
          value={deal.pipeline_stage || "lead"}
          onChange={(e) => onMove(e.target.value)}
          className="text-[10px] !p-1 flex-1 mr-1"
        >
          {STAGES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <button onClick={onBlast} className="text-[--ink-muted] hover:text-[--accent] p-1" title="Blast">
          <Send size={11} strokeWidth={1.5} />
        </button>
        <button onClick={onDelete} className="text-[--ink-muted] hover:text-[--danger] p-1" title="Delete">
          <Trash2 size={11} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
